import React, { FC, ReactNode } from 'react';
import { ButtonProps } from 'antd/lib/button';
import { Button, message, Modal, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  MinusSquareOutlined,
} from '@ant-design/icons/lib';
import { useMutation } from 'react-query';
import { PartnerHigoDeviceExcerpt } from 'models/higoDevice';
import { DevicesAPI } from 'api/DevicesAPI';
import { BusinessPartnerCtx } from 'models/businessPartner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface UnassignPartnerDeviceButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerCtx: BusinessPartnerCtx;
  device: PartnerHigoDeviceExcerpt;
  tooltip?: ReactNode;
  onUnassign?: () => void;
}

export const UnassignPartnerDeviceButton: FC<UnassignPartnerDeviceButtonProps> = ({
  businessPartnerCtx,
  device,
  onUnassign,
  tooltip = 'Unassign partner device',
  ...buttonProps
}) => {
  const [mutate, { isLoading }] = useMutation(DevicesAPI.unassignPartner);
  const intlMessage = useFormatMessage();

  const showConfirm = () =>
    Modal.confirm({
      title: intlMessage(
        { id: 'devices.unassign.partner.confirm' },
        {
          serialNumber: device.serialNumber,
          name: businessPartnerCtx.name,
        }
      ),
      icon: <ExclamationCircleOutlined />,
      okText: intlMessage({ id: 'yes' }),
      cancelText: intlMessage({ id: 'cancel' }),
      okType: 'danger',
      onOk: () =>
        mutate(
          { businessPartnerId: businessPartnerCtx.id, deviceId: device.id },
          {
            onSuccess: () => {
              message.success(
                intlMessage(
                  { id: 'devices.unassign.partner.success' },
                  {
                    serialNumber: device.serialNumber,
                    name: businessPartnerCtx.name,
                  }
                )
              );
              onUnassign && onUnassign();
            },
          }
        ),
    });

  const button = (
    <Button
      id="button-unassign-partner-device"
      onClick={showConfirm}
      loading={isLoading}
      icon={<MinusSquareOutlined />}
      {...buttonProps}
    />
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};
